import { For, Show } from "solid-js";
import { MapSourceProvenance } from "./MapSourceProvenance";
import { mapCountLabel } from "./mapPreviewConfig";
import type { MapPreviewModel } from "./mapPreviewModel";
import type { MapPlan } from "./mapTypes";

export function MapCurrentBaseline(props: {
  websiteId: string;
  plan: MapPlan;
  preview: MapPreviewModel;
}) {
  return (
    <div class="map-baseline-view">
      <section
        class="map-baseline-summary"
        aria-labelledby="map-baseline-title"
      >
        <div>
          <span class="signal-mini-kicker">
            Imported from Scan and Current Paths
          </span>
          <h2 id="map-baseline-title">Current estate baseline</h2>
          <p>
            A concise reference for architecture discussion, using the latest
            completed scan rather than a second copy of the Flows workspace.
          </p>
          <MapSourceProvenance source={props.preview.source} />
        </div>
        <dl>
          <BaselineMetric
            value={props.plan.data.pages.length}
            label="pages in inventory"
          />
          <BaselineMetric
            value={props.plan.currentGroups.length}
            label="current content areas"
          />
          <BaselineMetric
            value={props.preview.flowPreview.topLevel.length}
            label="current paths"
          />
          <BaselineMetric
            value={props.preview.highPriorityConcernCount}
            label="priority structural concerns"
          />
        </dl>
      </section>

      <div class="map-baseline-columns">
        <section class="map-baseline-section">
          <header class="map-section-head">
            <div>
              <span class="signal-mini-kicker">Current structural routes</span>
              <h2>Current Paths</h2>
            </div>
            <a
              href={`/w/${props.websiteId}/scan/pages?view=paths`}
              class="map-text-link"
            >
              Open Flows
            </a>
          </header>
          <Show
            when={props.preview.flowPreview.topLevel.length > 0}
            fallback={
              <p class="map-preview-empty">
                No supported current path can be derived from this scan.
              </p>
            }
          >
            <ol class="map-baseline-flow-list">
              <For each={props.preview.flowPreview.topLevel}>
                {(flow, index) => (
                  <li>
                    <span class="signal-rank">{index() + 1}</span>
                    <div>
                      <strong>{flow.label}</strong>
                      <p>{flow.priorityReason}</p>
                      <small>
                        {mapCountLabel(flow.uniquePages.length, "page")} ·{" "}
                        {mapCountLabel(flow.paths.length, "retained route")} ·{" "}
                        {mapCountLabel(flow.priorityConcernCount, "concern")}
                      </small>
                    </div>
                    <a
                      href={`/w/${props.websiteId}/scan/pages?view=paths&flow=${encodeURIComponent(
                        flow.representativePath.id,
                      )}`}
                    >
                      Inspect
                    </a>
                  </li>
                )}
              </For>
            </ol>
          </Show>
        </section>

        <section class="map-baseline-section">
          <header class="map-section-head">
            <div>
              <span class="signal-mini-kicker">Evidence concentration</span>
              <h2>Structural hotspots</h2>
            </div>
            <span>Not a new score</span>
          </header>
          <Show
            when={props.preview.hotspots.length > 0}
            fallback={
              <p class="map-preview-empty">
                No structural hotspot linked to a Current Path is present in
                this scan.
              </p>
            }
          >
            <ol class="map-hotspot-list">
              <For each={props.preview.hotspots}>
                {(group) => (
                  <li>
                    <div>
                      <strong>{group.label}</strong>
                      <small>
                        {mapCountLabel(group.pageCount, "page")} ·{" "}
                        {scoreLabel(group.averageScore)}
                      </small>
                    </div>
                    <dl>
                      <div>
                        <dt>Priority concerns</dt>
                        <dd>{group.highPriorityConcernCount}</dd>
                      </div>
                      <div>
                        <dt>No onward link</dt>
                        <dd>{group.noOnwardPageCount}</dd>
                      </div>
                    </dl>
                  </li>
                )}
              </For>
            </ol>
          </Show>
        </section>
      </div>

      <section class="map-baseline-section is-estate">
        <header class="map-section-head">
          <div>
            <span class="signal-mini-kicker">Grouped current estate</span>
            <h2>Content areas in the assessed inventory</h2>
          </div>
          <span>{mapCountLabel(props.preview.groups.length, "group")}</span>
        </header>
        <div class="map-baseline-groups">
          <For each={props.preview.groups}>
            {(group) => (
              <article>
                <strong>{group.label}</strong>
                <span class="map-baseline-page-count">
                  {mapCountLabel(group.pageCount, "page")}
                </span>
                <small>
                  {group.assessedCount}/{group.pageCount} assessed ·{" "}
                  {scoreLabel(group.averageScore)}
                </small>
                <small>
                  {group.concernCount}{" "}
                  {group.concernCount === 1 ? "concern" : "concerns"} linked to
                  Current Paths
                </small>
              </article>
            )}
          </For>
        </div>
      </section>
    </div>
  );
}

function BaselineMetric(props: { value: number; label: string }) {
  return (
    <div>
      <dd>{props.value}</dd>
      <dt>{props.label}</dt>
    </div>
  );
}

function scoreLabel(score: number | null): string {
  return score === null
    ? "Content Fitness unavailable"
    : `${score} average Content Fitness`;
}
